// The row model behind the shape editor: `output_schema` <-> a list of named, typed rows.
//
// A row editor rather than a JSON editor, and the reason is the whole feature. Renaming an answer
// field has to drag every jsonpath that reads it along, and a rename typed into a text blob is
// indistinguishable from a delete plus an add — the paths cannot follow what the editor cannot see.
// Rows carry a STABLE ID, so a rename is a row whose name changed and the paths can be rewritten
// from its old name.
//
// Deliberately narrow: an object of scalars, or an object of objects of scalars. That is both
// shipped examples (share_buyback_mentions_v1 is flat, peer_supply_chain_pulse_v1 is one level of
// nesting) and it is where the corpus lives. Everything else — arrays, a third level, a nullable
// union type — is NOT modelled and NOT flattened: `isModellable` returns false and the editor hands
// the schema to the JSON pane instead. A row editor that silently dropped a key would be worse than
// no row editor.
//
// `enum` IS modelled, and it is the exception on purpose. It is the one constraint strict mode
// actually enforces, so a categorical score becomes grammar-constrained rather than hoped for —
// which makes it the single most valuable thing to carry out of a prose contract and into a schema.
// Leaving it to the JSON pane would have meant the common conversion needed the escape hatch.
//
// Strict-mode invariants are held HERE, on the way out, rather than reported after the fact by the
// validator: every object closes itself (`additionalProperties: false`), and `required` lists every
// property, because strict mode allows no optional keys (validate.js:189, :198).

const MODELLABLE_KEYS = ['type', 'properties', 'required', 'additionalProperties', 'description', 'enum'];

// What a leaf row may be. `null` and `array` are allowed by the format (specSchema.js:254) and
// deliberately absent here: a nullable leaf is a union type, an array needs an `items` schema, and
// both belong to the JSON pane.
export const SCALAR_TYPES = ['string', 'number', 'integer', 'boolean'];
export const OBJECT_TYPE = 'object';
export const ROW_TYPES = SCALAR_TYPES.concat([OBJECT_TYPE]);

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

let uid = 0;
const nextId = () => {
  uid += 1;
  return `field-${uid}`;
};

export const newRow = (type = 'string') => ({
  id: nextId(),
  name: '',
  type,
  description: '',
  enumValues: null,
  children: null,
});

const hasOnlyModellableKeys = (node) => Object.keys(node).every((key) => MODELLABLE_KEYS.indexOf(key) > -1);

// A scalar leaf, with or without an `enum`. An enum whose members disagree with the declared type is
// still modellable — that is a validator error the operator has to see and fix, not a shape this
// editor should refuse to display.
const isScalarNode = (node) =>
  isObject(node) &&
  hasOnlyModellableKeys(node) &&
  SCALAR_TYPES.indexOf(node.type) > -1 &&
  (node.enum === undefined || Array.isArray(node.enum));

const isObjectNode = (node) =>
  isObject(node) && hasOnlyModellableKeys(node) && node.type === OBJECT_TYPE && isObject(node.properties);

// True when the row editor can represent this schema without losing anything. Note it says nothing
// about whether the schema is VALID — a source missing `additionalProperties: false` is modellable,
// and `toSchema` is what puts it back (reported as a normalisation, never silently).
export const isModellable = (schema) => {
  if (!isObjectNode(schema)) return false;
  const properties = schema.properties;
  const keys = Object.keys(properties);
  if (keys.length === 0) return false;

  return keys.every((key) => {
    const node = properties[key];
    if (isScalarNode(node)) return true;
    if (!isObjectNode(node)) return false;
    const childKeys = Object.keys(node.properties);
    // Depth stops here: a grandchild object would be a third level.
    return childKeys.length > 0 && childKeys.every((childKey) => isScalarNode(node.properties[childKey]));
  });
};

const rowFor = (name, node) => ({
  id: nextId(),
  name,
  type: node.type,
  description: typeof node.description === 'string' ? node.description : '',
  enumValues: Array.isArray(node.enum) ? node.enum.slice() : null,
  children: isObjectNode(node) ? Object.keys(node.properties).map((key) => rowFor(key, node.properties[key])) : null,
});

// Fresh ids on every call, so this runs ONCE per source (at seed time) and never inside a render.
// Re-deriving rows on each keystroke would remount every input mid-edit.
export const toRows = (schema) =>
  isModellable(schema) ? Object.keys(schema.properties).map((key) => rowFor(key, schema.properties[key])) : [];

export const rootDescriptionOf = (schema) =>
  isObject(schema) && typeof schema.description === 'string' ? schema.description : '';

const nodeFor = (row) => {
  const node = { type: row.type };
  if (row.description) node.description = row.description;
  // An empty list is not a declaration of no values — strict mode rejects an empty enum — so it is
  // dropped rather than emitted.
  if (Array.isArray(row.enumValues) && row.enumValues.length > 0) node.enum = row.enumValues.slice();
  if (row.type !== OBJECT_TYPE) return node;

  const children = Array.isArray(row.children) ? row.children : [];
  const named = children.filter((child) => child.name);
  node.properties = {};
  named.forEach((child) => {
    node.properties[child.name] = nodeFor(child);
  });
  node.required = named.map((child) => child.name);
  node.additionalProperties = false;
  return node;
};

// Rows -> schema, with the strict-mode invariants applied. Unnamed rows are DROPPED rather than
// written as an empty key: a half-typed row must not produce `{"": {...}}` in the payload. The
// resulting hole (an object with no properties) is left for the validator to report, which is the
// same treatment the advanced editor gives it.
export const toSchema = (rows, rootDescription) => {
  const schema = { type: OBJECT_TYPE };
  if (rootDescription) schema.description = rootDescription;
  const named = (rows || []).filter((row) => row.name);
  schema.properties = {};
  named.forEach((row) => {
    schema.properties[row.name] = nodeFor(row);
  });
  schema.required = named.map((row) => row.name);
  schema.additionalProperties = false;
  return schema;
};

// What `toSchema` would silently put right about a source schema, in words, so opening the editor
// on a legacy shape reports the fix instead of performing it invisibly.
export const schemaInvariantGaps = (schema) => {
  const gaps = [];

  const walk = (node, label) => {
    if (!isObjectNode(node)) return;
    const keys = Object.keys(node.properties);
    const required = Array.isArray(node.required) ? node.required : [];
    const missing = keys.filter((key) => required.indexOf(key) === -1);
    if (missing.length > 0) {
      gaps.push(
        `Listed ${missing
          .map((m) => `\`${m}\``)
          .join(', ')} in ${label} \`required\` — strict mode allows no optional keys.`
      );
    }
    if (node.additionalProperties !== false) {
      gaps.push(`Closed the ${label} object with \`additionalProperties: false\` — strict mode requires it.`);
    }
    keys.forEach((key) => walk(node.properties[key], `\`${key}\``));
  };

  walk(schema, 'the answer');
  return gaps;
};
